import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate, useLoaderData } from 'react-router-dom';
import { fetchCategories } from '../api/contractorProfile';
import { Category } from '../types/contractor';

const PRIVACY_POLICY_VERSION = '1.0';

export async function registerLoader() {
  const categories = await fetchCategories();
  return { categories };
}

type Step = 'choose' | 'client' | 'contractor';

interface RegisterProps {
  // Set when arriving from a Direct Job invite - skips the choose step and locks the email,
  // since the invite is tied to a specific address.
  initialEmail?: string;
  lockToClientStep?: boolean;
}

const Register: React.FC<RegisterProps> = ({ initialEmail, lockToClientStep }) => {
  const { categories } = useLoaderData() as { categories: Category[] };
  const [step, setStep] = useState<Step>(lockToClientStep ? 'client' : 'choose');

  // Shared fields
  const [email, setEmail] = useState(initialEmail ?? '');
  const [password, setPassword] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [error, setError] = useState('');
  const [registeredEmail, setRegisteredEmail] = useState('');

  // Contractor-only fields
  const [companyName, setCompanyName] = useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);

  const { currentUser, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser) navigate('/');
  }, [currentUser, navigate]);

  const toggleCategory = (id: number) => {
    setSelectedCategoryIds(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const isContractor = step === 'contractor';

    if (isContractor && selectedCategoryIds.length === 0) {
      setError('Please select at least one service category.');
      return;
    }

    const result = await register(
      email,
      password,
      isContractor,
      PRIVACY_POLICY_VERSION,
      marketingOptIn,
      isContractor
        ? {
            companyName,
            categoryIds: selectedCategoryIds,
          }
        : undefined
    );

    if (result.success) {
      setRegisteredEmail(email);
    } else {
      setError(result.message || 'Registration failed.');
    }
  };

  if (registeredEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-surface-container-lowest border border-outline-variant rounded-2xl p-8 text-center space-y-4">
            <span className="material-symbols-outlined text-[48px] text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>mark_email_unread</span>
            <h2 className="text-2xl font-bold text-on-surface">Check your email</h2>
            <p className="text-on-surface-variant text-sm leading-relaxed">
              We sent a confirmation link to <span className="font-medium text-on-surface">{registeredEmail}</span>.
              Open the email and click the link to activate your account.
            </p>
            <p className="text-xs text-on-surface-variant">
              Can't find it? Check your spam folder. The link expires in 7 days.
            </p>
            <Link
              to="/login"
              className="inline-block mt-2 w-full py-3 px-4 bg-primary text-on-primary font-semibold rounded-xl hover:brightness-110 transition-all text-center text-sm"
            >
              Go to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Link to="/" className="text-primary hover:opacity-80 text-sm font-medium">
            &larr; Back to Home
          </Link>
        </div>

        {step === 'choose' && (
          <>
            <div>
              <h2 className="mt-6 text-center text-3xl font-bold text-on-surface">Create an account</h2>
              <p className="mt-2 text-center text-sm text-on-surface-variant">How will you be using Buildon?</p>
            </div>
            <div className="space-y-4">
              <button
                onClick={() => setStep('client')}
                className="w-full flex items-center gap-4 p-5 border-2 border-outline-variant rounded-xl hover:border-primary hover:bg-primary/5 transition-all text-left group"
              >
                <span className="material-symbols-outlined text-[36px] text-primary flex-shrink-0" style={{ fontVariationSettings: "'FILL' 1" }} data-icon="search">search</span>
                <div>
                  <p className="font-semibold text-on-surface group-hover:text-primary transition-colors">I need to hire a contractor</p>
                  <p className="text-sm text-on-surface-variant mt-0.5">Post jobs and find verified professionals</p>
                </div>
              </button>
              <button
                onClick={() => setStep('contractor')}
                className="w-full flex items-center gap-4 p-5 border-2 border-outline-variant rounded-xl hover:border-primary hover:bg-primary/5 transition-all text-left group"
              >
                <span className="material-symbols-outlined text-[36px] text-primary flex-shrink-0" style={{ fontVariationSettings: "'FILL' 1" }} data-icon="construction">construction</span>
                <div>
                  <p className="font-semibold text-on-surface group-hover:text-primary transition-colors">I'm a contractor</p>
                  <p className="text-sm text-on-surface-variant mt-0.5">List your services and find work</p>
                </div>
              </button>
            </div>
            <div className="text-center text-sm">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-primary hover:opacity-80">Sign in</Link>
            </div>
          </>
        )}

        {(step === 'client' || step === 'contractor') && (
          <>
            <div>
              {!lockToClientStep && (
                <button
                  onClick={() => setStep('choose')}
                  className="text-sm text-on-surface-variant hover:text-on-surface flex items-center gap-1 mb-4"
                >
                  <span className="material-symbols-outlined text-[16px]">arrow_back</span>
                  Back
                </button>
              )}
              <h2 className="text-2xl font-bold text-on-surface">
                {step === 'contractor' ? 'Create a contractor account' : 'Create a client account'}
              </h2>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label className="block text-sm font-medium text-on-surface mb-1">Email address</label>
                <input
                  type="email"
                  required
                  readOnly={!!initialEmail}
                  className={`w-full px-4 py-3 border border-outline-variant rounded-xl bg-surface-container-lowest text-on-surface focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${initialEmail ? 'opacity-70 cursor-not-allowed' : ''}`}
                  placeholder="you@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface mb-1">Password</label>
                <input
                  type="password"
                  required
                  className="w-full px-4 py-3 border border-outline-variant rounded-xl bg-surface-container-lowest text-on-surface focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Choose a password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
              </div>

              {step === 'contractor' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-1">Company name</label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-outline-variant rounded-xl bg-surface-container-lowest text-on-surface focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Your company or trading name"
                      value={companyName}
                      onChange={e => setCompanyName(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">
                      Service categories <span className="text-error">*</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {categories.map(c => {
                        const selected = selectedCategoryIds.includes(c.id);
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => toggleCategory(c.id)}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                              selected
                                ? 'bg-primary text-on-primary border-primary'
                                : 'bg-surface text-on-surface-variant border-outline-variant hover:border-primary hover:text-primary'
                            }`}
                          >
                            {selected && <span className="mr-1">✓</span>}
                            {c.name}
                          </button>
                        );
                      })}
                    </div>
                    {selectedCategoryIds.length === 0 && (
                      <p className="text-xs text-on-surface-variant mt-1.5">Select at least one category.</p>
                    )}
                  </div>
                </>
              )}

              <div className="flex items-start gap-3 rounded-xl border border-outline-variant bg-surface-container-low p-4">
                <input
                  id="marketing-opt-in"
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary border-outline-variant rounded"
                  checked={marketingOptIn}
                  onChange={e => setMarketingOptIn(e.target.checked)}
                />
                <label htmlFor="marketing-opt-in" className="text-sm text-on-surface-variant leading-relaxed">
                  <span className="font-medium text-on-surface">Optional:</span> Send me occasional tips and platform updates. You can change this at any time.
                </label>
              </div>

              <div className="flex items-start gap-3 rounded-xl border border-outline-variant bg-surface-container-low p-4">
                <input
                  id="popia-consent"
                  type="checkbox"
                  required
                  className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary border-outline-variant rounded"
                  checked={consentGiven}
                  onChange={e => setConsentGiven(e.target.checked)}
                />
                <label htmlFor="popia-consent" className="text-sm text-on-surface-variant leading-relaxed">
                  I have read and agree to the{' '}
                  <Link to="/privacy-policy" target="_blank" className="font-medium text-primary hover:underline">Privacy Policy</Link>
                  {' '}and{' '}
                  <Link to="/terms" target="_blank" className="font-medium text-primary hover:underline">Terms of Service</Link>.
                  I consent to Buildon collecting and processing my personal information as described.
                </label>
              </div>

              {error && <p className="text-sm text-error text-center">{error}</p>}

              <button
                type="submit"
                disabled={!consentGiven}
                className="w-full py-3 px-4 bg-primary text-on-primary font-semibold rounded-xl hover:brightness-110 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create account
              </button>

              <div className="text-center text-sm text-on-surface-variant">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-primary hover:opacity-80">Sign in</Link>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default Register;
